import DOMPurify from "dompurify";
import { useTranslation } from "react-i18next";
import { marked } from "marked";
import { useEffect, useMemo, useRef, useState } from "react";

/**
 * Markdown: rendered safely, and edited side by side with its preview.
 *
 * SANITISED, ALWAYS. Markdown permits raw HTML, so rendering it is running
 * whatever the last editor typed in the next reader's browser. This service has
 * ONE shared password, so "only trusted people can edit" means "everyone who
 * has the password, plus anyone who ever had it" - which is not a trust
 * boundary worth betting a session cookie on. DOMPurify strips scripts and
 * event handlers; the cost is nothing anybody writing bench notes will notice.
 *
 * A split pane rather than a WYSIWYG. Markdown stays the stored truth - it goes
 * into git-shaped workflows, diffs readably and survives this UI being replaced
 * - and a rich editor would put a large dependency between the author and the
 * text for the sake of hiding four characters of syntax.
 */

const RENDER_OPTS = { breaks: true, gfm: true } as const;

export function renderMarkdown(md: string): string {
  const raw = marked.parse(md || "", { ...RENDER_OPTS, async: false }) as string;
  return DOMPurify.sanitize(raw, {
    // Links may leave; they must not carry the referrer or gain window.opener.
    ADD_ATTR: ["target", "rel"],
  });
}

export function MarkdownView({
  md,
  empty,
  collapsedPx,
}: {
  md: string;
  empty?: string;
  /** Show this much, then a "Show all" control. Omit for no collapsing. */
  collapsedPx?: number;
}) {
  const { t } = useTranslation();
  const html = useMemo(() => renderMarkdown(md), [md]);
  const body = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [overflows, setOverflows] = useState(false);

  // MEASURED, not guessed from the source length. A bench page is mostly
  // tables, and the relationship between characters of Markdown and rendered
  // height is not a ratio worth inventing - a short note full of table rows is
  // tall, and a long paragraph is not. Measuring also means the control appears
  // only when there is genuinely something hidden, so a two-line note does not
  // grow a "Show all" button that reveals nothing.
  useEffect(() => {
    if (!collapsedPx || !body.current) return;
    const measure = () =>
      setOverflows((body.current?.scrollHeight ?? 0) > collapsedPx + 24);
    measure();
    // Re-measure on resize: the same note wraps differently on a phone, and a
    // control that was right at one width is wrong at another.
    const ro = new ResizeObserver(measure);
    ro.observe(body.current);
    return () => ro.disconnect();
  }, [collapsedPx, html]);

  if (!md.trim()) {
    return (
      <p className="text-sm italic text-slate-500">
        {empty ?? t("markdown.empty")}
      </p>
    );
  }

  const clamp = collapsedPx && overflows && !open;
  return (
    <div>
      <div className="relative">
        <div
          ref={body}
          className="prose-hil"
          style={clamp ? { maxHeight: collapsedPx, overflow: "hidden" } : undefined}
          dangerouslySetInnerHTML={{ __html: html }}
        />
        {clamp && (
          // Fades into the page background rather than a fixed colour, so it
          // works in both themes without a second rule.
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-b from-transparent to-white dark:to-slate-900"
          />
        )}
      </div>
      {collapsedPx && overflows && (
        <button
          className="mt-2 rounded border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
        >
          {open ? t("markdown.showLess") : t("markdown.showAll")}
        </button>
      )}
    </div>
  );
}

export function MarkdownEditor({
  value,
  onSave,
  onCancel,
  label,
  busy = false,
}: {
  value: string;
  onSave: (md: string) => void;
  onCancel: () => void;
  /** What is being edited, in the caller's words. Defaults to "Markdown"
   *  from the catalogue - a default argument cannot, because it would be
   *  evaluated before a language is chosen. */
  label?: string;
  busy?: boolean;
}) {
  const { t } = useTranslation();
  const [md, setMd] = useState(value);
  const [tab, setTab] = useState<"write" | "preview" | "split">("split");
  const ta = useRef<HTMLTextAreaElement>(null);

  // Ctrl/Cmd+Enter saves. A long document behind a mouse-only Save is a
  // document people stop updating.
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        onSave(md);
      }
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [md, onSave, onCancel]);

  function tabKey(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    // Tab indents instead of leaving the field. Losing a half-written document
    // to a stray Tab is the kind of thing that stops people writing them.
    if (e.key !== "Tab") return;
    e.preventDefault();
    const el = e.currentTarget;
    const { selectionStart: s, selectionEnd: en } = el;
    const next = md.slice(0, s) + "  " + md.slice(en);
    setMd(next);
    requestAnimationFrame(() => el.setSelectionRange(s + 2, s + 2));
  }

  const dirty = md !== value;

  return (
    <div className="rounded-lg border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 px-3 py-2 dark:border-slate-800">
        <span className="text-sm font-medium">{label ?? t("markdown.label")}</span>
        <div className="ml-auto flex gap-1">
          {(
            [
              ["write", "markdown.tabWrite"],
              ["split", "markdown.tabSplit"],
              ["preview", "markdown.tabPreview"],
            ] as const
          ).map(([name, key]) => (
            <button
              key={name}
              type="button"
              onClick={() => setTab(name)}
              className={`rounded px-2 py-1 text-xs ${
                tab === name
                  ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900"
                  : "text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
              }`}
            >
              {t(key)}
            </button>
          ))}
        </div>
      </div>

      <div
        className={`grid ${tab === "split" ? "sm:grid-cols-2" : "grid-cols-1"} divide-slate-200 dark:divide-slate-800 sm:divide-x`}
      >
        {tab !== "preview" && (
          <textarea
            ref={ta}
            value={md}
            onChange={(e) => setMd(e.target.value)}
            onKeyDown={tabKey}
            spellCheck={false}
            className="min-h-[22rem] w-full resize-y bg-transparent p-3 font-mono text-[13px] leading-relaxed"
            placeholder={t("markdown.placeholder")}
          />
        )}
        {tab !== "write" && (
          <div className="min-h-[22rem] overflow-auto p-3">
            <MarkdownView md={md} empty={t("markdown.previewEmpty")} />
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 border-t border-slate-200 px-3 py-2 dark:border-slate-800">
        <span className="text-xs text-slate-500">
          {dirty ? t("markdown.dirty") : t("markdown.saved")} ·{" "}
          {t("markdown.shortcuts")}
        </span>
        <div className="ml-auto flex gap-2">
          <button type="button" className="px-3 py-1.5 text-sm" onClick={onCancel}>
            {t("common.cancel")}
          </button>
          <button
            type="button"
            disabled={busy || !dirty}
            onClick={() => onSave(md)}
            className="rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-40 dark:bg-slate-100 dark:text-slate-900"
          >
            {busy ? t("common.saving") : t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}
